import { Avatar, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import type { AvatarType } from './avatarTypes';
import { Emoji } from './Emoji';
import { colors } from '@sweep-io/sweep-design';

export interface AvatarPictureProps {
  avatar?: AvatarType;
  dimension: number;
  clickable?: boolean;
  greyscale?: boolean;
  isInactive: boolean;
}

const AvatarPicture = ({
  avatar = {},
  dimension,
  clickable,
  greyscale,
  isInactive,
}: AvatarPictureProps) => {
  const { imageUrl, emoji } = avatar;
  const [imageLoadState, setImageLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    //reload when imageUrl changes
    if (imageUrl) {
      setImageLoadState('loading');
    }
  }, [imageUrl]);

  const handleImageLoad = () => {
    setImageLoadState('loaded');
  };

  const handleImageError = () => {
    setImageLoadState('error');
  };

  const isError = imageLoadState === 'error';
  const isLoaded = imageLoadState === 'loaded';

  // Always render image if there's an imageUrl (so handlers can fire)
  const shouldRenderImage = !isInactive && imageUrl;
  // Only show image to user when it's actually loaded
  const shouldShowImageToUser = shouldRenderImage && isLoaded;
  // Show emoji when: no imageUrl, OR loading, OR error
  const shouldShowEmoji = !isInactive && (!imageUrl || isError) && emoji;

  return (
    <Box sx={{ filter: greyscale ? 'grayscale(100%)' : 'none' }}>
      {isInactive && (
        <Box
          data-testid="profile-picture-avatar"
          sx={{
            width: dimension,
            height: dimension,
            borderRadius: '40px',
            backgroundColor: colors.grey[100],
            border: `1px solid ${colors.grey[120]}`,
          }}
        />
      )}

      {shouldRenderImage && (
        <Box
          sx={{
            width: dimension,
            height: dimension,
            borderRadius: '50%',
            overflow: 'hidden',
            cursor: clickable ? 'pointer' : 'default',
            // Hide the image container until loaded
            display: shouldShowImageToUser ? 'block' : 'none',
          }}
        >
          <Avatar
            key={imageUrl}
            data-testid="profile-picture-avatar"
            src={imageUrl}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt="Avatar"
          />
        </Box>
      )}

      {shouldShowEmoji && (
        <Emoji
          content={emoji.content}
          bgColor={emoji.bgColor}
          dimension={dimension}
          cursor={clickable ? 'pointer' : 'default'}
        />
      )}
    </Box>
  );
};

export { AvatarPicture };
